import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api, ENDPOINTS } from '../services';
import {
  clearPersistedSession,
  getPersistedAuthState,
  writePersistedSession
} from '../shared/authSession';

const persistedAuthState = getPersistedAuthState();

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await api.post(ENDPOINTS.LOGIN, {
        email,
        password,
      });
      if (!response?.data?.data || !response.data.data.accessToken) {
        throw new Error('Invalid response format');
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Login failed');
    }
  }
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await api.post(ENDPOINTS.FORGOT_PASSWORD, {
        email,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || 'Failed to send reset link');
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ token, new_password }, { rejectWithValue }) => {
    try {
      const response = await api.post(`${ENDPOINTS.RESET_PASSWORD}?token=${token}`, {
        new_password,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Password reset failed');
    }
  }
);

export const fetchTenantInfo = createAsyncThunk(
  'auth/fetchTenantInfo',
  async (domain, { rejectWithValue }) => {
    try {
      const response = await api.get(ENDPOINTS.TENANT_INFO(domain));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch tenant info');
    }
  }
);

export const ssoLogin = createAsyncThunk(
  'auth/ssoLogin',
  async (ssoData, { rejectWithValue }) => {
    try {
      const response = await api.post(ENDPOINTS.SSO_LOGIN, ssoData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'SSO login failed');
    }
  }
);

// Google SSO thunk — sends the Google token returned by the configured
// frontend flow to /auth/login/sso/google. The backend validates the token
// server-side and returns the same JWT pair as every other login method.
export const googleSsoLogin = createAsyncThunk(
  'auth/googleSsoLogin',
  async (ssoData, { rejectWithValue }) => {
    try {
      const response = await api.post(ENDPOINTS.SSO_LOGIN_PROVIDER('google'), ssoData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Google SSO login failed');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    ...persistedAuthState,
    tenantInfo: null,
    isLoading: false,
    ssoLoading: false,
    // Separate loading flag for Google SSO so the Google button can be
    // disabled independently of the Microsoft SSO button.
    googleSsoLoading: false,
    error: null,
    isAuthenticated: persistedAuthState.isAuthenticated,
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      clearPersistedSession();
    },
    clearError: (state) => {
      state.error = null;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    updateAccessToken: (state, action) => {
      state.accessToken = action.payload;
    },
    rehydrateSession: (state) => {
      const persisted = getPersistedAuthState();
      state.user = persisted.user;
      state.accessToken = persisted.accessToken;
      state.refreshToken = persisted.refreshToken;
      state.isAuthenticated = persisted.isAuthenticated;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload?.data && action.payload.data.accessToken) {
          state.user = action.payload.data;
          state.accessToken = action.payload.data.accessToken;
          state.refreshToken = action.payload.data.refreshToken || null;
          state.isAuthenticated = true;
          const sessionData = {
            user: action.payload.data,
            accessToken: action.payload.data.accessToken,
            refreshToken: action.payload.data.refreshToken || null
          };
          writePersistedSession(sessionData);
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(forgotPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchTenantInfo.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTenantInfo.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tenantInfo = action.payload.data;
      })
      .addCase(fetchTenantInfo.rejected, (state, action) => {
        state.isLoading = false;
        // Don't overwrite login errors with tenant info errors
        if (!state.error) {
          state.error = action.payload;
        }
      })
      .addCase(ssoLogin.pending, (state) => {
        state.ssoLoading = true;
        state.error = null;
      })
      .addCase(ssoLogin.fulfilled, (state, action) => {
        state.ssoLoading = false;
        if (action.payload?.data) {
          state.user = action.payload.data;
          state.accessToken = action.payload.data.accessToken;
          state.refreshToken = action.payload.data.refreshToken;
          state.isAuthenticated = true;
          const sessionData = {
            user: action.payload.data,
            accessToken: action.payload.data.accessToken,
            refreshToken: action.payload.data.refreshToken
          };
          writePersistedSession(sessionData);
        }
      })
      .addCase(ssoLogin.rejected, (state, action) => {
        state.ssoLoading = false;
        state.error = action.payload;
      })
      // Google SSO reducers — mirror the Microsoft ssoLogin reducers exactly.
      // googleSsoLoading is used to disable the Google button while the request
      // is in flight, preventing double-clicks (per design doc edge case).
      .addCase(googleSsoLogin.pending, (state) => {
        state.googleSsoLoading = true;
        state.error = null;
      })
      .addCase(googleSsoLogin.fulfilled, (state, action) => {
        state.googleSsoLoading = false;
        if (action.payload?.data) {
          state.user = action.payload.data;
          state.accessToken = action.payload.data.accessToken;
          state.refreshToken = action.payload.data.refreshToken;
          state.isAuthenticated = true;
          const sessionData = {
            user: action.payload.data,
            accessToken: action.payload.data.accessToken,
            refreshToken: action.payload.data.refreshToken
          };
          writePersistedSession(sessionData);
        }
      })
      .addCase(googleSsoLogin.rejected, (state, action) => {
        state.googleSsoLoading = false;
        state.error = action.payload;
      });
  },
});

export const { logout, clearError, setError, updateAccessToken, rehydrateSession } = authSlice.actions;
export default authSlice.reducer;
